import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assertAndContinue} from 'Asserts';
import * as ToastActions from 'nav/toasts/ToastActions';
import {KEEP_TOAST_OPEN, ToastType} from 'nav/toasts/ToastConstants';
import ToastMessage from 'nav/toasts/ToastMessage';
import notifications from 'notifications';
import {ExecutionPlan} from 'studies/progress/Types';
import * as TrayTaskActions from 'tray/TrayTaskActions';
import * as TrayTaskConstants from 'tray/TrayTaskConstants';
import {GENERIC_ERROR_MESSAGE, GENERIC_SHORT_ERROR_MESSAGE} from 'ui-library/ErrorConstants';
import isFocusSupercededByModal from 'ui-library/modals/isFocusSupercededByModal';
import {BasePendingTask} from 'util/resume-persisted-tasks/PersistedTaskTypes';
import {clearTask, fetchPersistedTaskStatus} from 'util/resume-persisted-tasks/ResumePersistedTasks';
import * as TaskRunner from 'util/TaskRunner';
import {flagTaskError, getFailedTaskErrorResponse} from 'util/TaskRunner';
import {assertNever} from 'util/Types';

/**
 * Maximum time to wait for async creation to complete before showing a timeout message.
 *
 * {task_name}_short has LARGE max_processing_time (9min) and HIGH max_expected_wait_time (10min).
 * {task_name}_long has LARGE max_processing_time (29min), and empirically we tolerate longer wait times.
 *
 * To be fairly conservative, we assume {task_name}_long with 30min wait time. Note that this
 * is imperfect and during a queue spike, the creation may still succeed despite the front-end
 * showing a timeout message. This is something we should consider revisiting.
 */
export const MAX_ASYNC_WAIT_TIME_MS = 59 * 60 * 1000;

export const TASK_STATUS_ERROR_MESSAGE =
  'Please refresh the page or reach out to support@benchling.com if the issue persists.';

export interface OnUnknownErrorAction {
  /** The text of the action button. */
  text: string;
  /** The error description to be shown on the toast. */
  description: JSX.Element;
  /** The action to take when the action button is clicked. */
  onClick: () => void;
}

export interface CreationTrayDescriptors {
  /** An element that describes what is being created. To be used in the progress and success toasts. */
  creation: JSX.Element;
  /** Text to be displayed in the header of error toasts when the creation fails. */
  creationErrorHeader: string;

  /** Text to be displayed in the header of error toasts when polling for the task status fails. */
  taskStatusErrorHeader: string;
}

export interface CreationTrayCallbacks<TTaskStatus> {
  onSuccess: (response: TTaskStatus) => void;
  onOpen: (response: TTaskStatus) => void;
  onUnknownError: OnUnknownErrorAction;
  onRetry?: () => void;
}

/**
 * Shows a tray task to track the progress of an asynchronous creation.
 *
 * @param persistedPendingTask Specify the persisted task status when resuming polling
 */
export function showCreationTrayTask<TType extends string, TRequestMetadata, TTaskStatus>(
  taskId: string,
  statusUrl: string,
  taskMeta: TaskRunner.TaskMeta,
  descriptors: CreationTrayDescriptors,
  callbacks: CreationTrayCallbacks<TTaskStatus>,
  timingMeta?: ExecutionPlan,
  persistedPendingTask?: BasePendingTask<TType, TRequestMetadata, TTaskStatus>
): void {
  const trayTaskId = TrayTaskActions.createTrayTask({
    heading: (
      <>
        <ToastMessage
          body={<>Creating {descriptors.creation}...</>}
          progress={TrayTaskConstants.INDETERMINATE_PROGRESS}
          totalTimeMs={timingMeta?.estimatedTime}
        />
      </>
    ),
    progress: TrayTaskConstants.HIDE_PROGRESS,
  });

  if (persistedPendingTask?.response.status === 'SUCCESS') {
    showSuccessToast(taskId, trayTaskId, descriptors, callbacks, persistedPendingTask.response.result);
    return;
  }

  if (persistedPendingTask?.response.status === 'FAILURE') {
    showErrorToast(
      taskId,
      trayTaskId,
      descriptors,
      callbacks,
      getFailedTaskErrorResponse(persistedPendingTask.response)
    );
    return;
  }

  TaskRunner.resumeTask<TTaskStatus>(statusUrl, {
    maxTotalWaitTime: MAX_ASYNC_WAIT_TIME_MS,
    taskMeta,
    taskRequestDone: 0,
    onPendingStatus: async (deferred, retry, pollStats) => {
      const persistedTaskStatus = await fetchPersistedTaskStatus(taskId);
      if (!persistedTaskStatus) {
        deferred.reject(flagTaskError({invalidTask: true}, taskMeta));
        return;
      }
      switch (persistedTaskStatus.response.status) {
        case 'SUCCESS':
          // SUCCESS -> PENDING: The task succeeded while the app was suspended (ex. user closed their laptop)
          deferred.resolve(persistedTaskStatus.response.result);
          break;
        case 'FAILURE':
          // FAILURE -> PENDING: The task failed while the app was suspended (ex. user closed their laptop)
          deferred.reject(flagTaskError(getFailedTaskErrorResponse(persistedTaskStatus.response), taskMeta));
          break;
        case 'STARTED':
          // STARTED -> PENDING: The task started and is now unknown, likely OOM or other unexpected error
          deferred.reject(flagTaskError({invalidTask: true}, taskMeta));
          break;
        case 'QUEUED':
          // QUEUED -> PENDING: The task has been pending for longer than Celery expire_results seconds,
          // so the task status has expired in redis. Keep polling.
          retry(pollStats.failRetries);
          break;
        case 'PENDING':
          // A persisted state of PENDING is not expected, task status is unknown
          deferred.reject(flagTaskError({invalidTask: true}, taskMeta));
          break;
        default:
          assertAndContinue(false, assertNever(persistedTaskStatus.response));
      }
    },
  })
    .progress((progress: number) => {
      TrayTaskActions.updateTrayTask(trayTaskId, {
        heading: (
          <>
            <ToastMessage
              body={<>Creating {descriptors.creation}...</>}
              progress={progress}
              totalTimeMs={timingMeta?.estimatedTime}
            />
          </>
        ),
        // Hide "native" percentage, since ToastMessage has a better UI when totalTimeMs is provided.
        progress: TrayTaskConstants.HIDE_PROGRESS,
      });
    })
    .done((response: TTaskStatus) => {
      showSuccessToast(taskId, trayTaskId, descriptors, callbacks, response);
    })
    .fail((response) => {
      showErrorToast(taskId, trayTaskId, descriptors, callbacks, response);
    });
}

function showSuccessToast<TTaskStatus>(
  taskId: string,
  trayTaskId: string,
  descriptors: CreationTrayDescriptors,
  callbacks: CreationTrayCallbacks<TTaskStatus>,
  response: TTaskStatus
): void {
  clearTask(taskId);
  callbacks.onSuccess(response);
  TrayTaskActions.updateTrayTask(trayTaskId, {
    progress: TrayTaskConstants.SUCCESS_PROGRESS,
    heading: <ToastMessage body={<>Created {descriptors.creation} successfully.</>} progress={100} />,
    onHeadingClick: () => {
      callbacks.onOpen(response);
    },
  });
}

function showErrorToast<TTaskStatus>(
  taskId: string,
  trayTaskId: string,
  descriptors: CreationTrayDescriptors,
  callbacks: CreationTrayCallbacks<TTaskStatus>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  response: any
): void {
  const isPollingError = response.taskStatus !== 'FAILURE' && !response.invalidTask;

  // Update subscription, if relevant
  if (!isPollingError) {
    // If the task has reached a terminal state, unsubscribe from the task
    clearTask(taskId);
  }

  // Notify user of outcome
  const retryAction = {
    actionText: 'Retry...',
    onActionClick: () => {
      // This is not the best UX. Ideally, if a new modal was opened, the original toast would
      // be blocked from interaction until the modal was closed
      if (isFocusSupercededByModal()) {
        ToastActions.pushToast({
          message: 'Please close the open modal before retrying.',
          type: ToastType.INFO,
        });
        return KEEP_TOAST_OPEN;
      } else {
        callbacks.onRetry?.();
        return undefined;
      }
    },
  };
  const viewAction = {
    actionText: callbacks.onUnknownError.text,
    onActionClick: () => {
      callbacks.onUnknownError.onClick();
      return KEEP_TOAST_OPEN;
    },
  };
  if (isPollingError) {
    TrayTaskActions.updateTrayTask(trayTaskId, {
      progress: TrayTaskConstants.FAILURE_PROGRESS,
      error: (
        <ToastMessage
          header={descriptors.taskStatusErrorHeader}
          body=<>
            {notifications.getFriendlyError(response) ?? `${GENERIC_SHORT_ERROR_MESSAGE}.`}
            <div className={css(styles.errorMessage)}>{TASK_STATUS_ERROR_MESSAGE}</div>
          </>
        />
      ),
    });
  } else if (response.status && response.status < 500) {
    TrayTaskActions.updateTrayTask(trayTaskId, {
      progress: TrayTaskConstants.FAILURE_PROGRESS,
      error: (
        <ToastMessage
          header={descriptors.creationErrorHeader}
          body={notifications.getFriendlyError(response) ?? GENERIC_ERROR_MESSAGE}
        />
      ),
      actions: callbacks.onRetry ? [retryAction] : [],
    });
  } else {
    TrayTaskActions.updateTrayTask(trayTaskId, {
      progress: TrayTaskConstants.FAILURE_PROGRESS,
      error: (
        <ToastMessage
          header={descriptors.creationErrorHeader}
          body={
            <>
              {notifications.getFriendlyError(response) ?? GENERIC_ERROR_MESSAGE}
              <div className={css(styles.errorMessage)}>{callbacks.onUnknownError.description}</div>
            </>
          }
        />
      ),
      actions: [viewAction, ...(callbacks.onRetry ? [retryAction] : [])],
    });
  }
}

const styles = StyleSheet.create({
  errorMessage: {
    marginTop: '10px',
  },
});
